import { useEffect } from "react";

/**
 * Runtime SEO setter. Updates <title>, per-route <meta> and <link rel="canonical">
 * in document.head so that:
 *  - Client-side navigation shows the correct browser tab title
 *  - The build-time prerenderer (scripts/prerender.js) captures per-route
 *    values into the static HTML that crawlers see without JS.
 *  - Canonical & og:url always reflect the actual route (fixes Google
 *    treating subpages as duplicates of the homepage).
 */
const SITE_ORIGIN = "https://hype-hafen.de";

function upsertMeta(selector, attrName, attrValue, content) {
  if (typeof document === "undefined") return;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attrName, attrValue);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  if (typeof document === "undefined") return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export function useSeo({
  title,
  description,
  ogTitle,
  ogDescription,
  twitterTitle,
  twitterDescription,
  canonical,
}) {
  useEffect(() => {
    if (title) document.title = title;

    const _ogTitle = ogTitle || title;
    const _ogDesc = ogDescription || description;
    const _twTitle = twitterTitle || title;
    const _twDesc = twitterDescription || description;

    if (description) {
      upsertMeta('meta[name="description"]', "name", "description", description);
    }
    if (_ogTitle) {
      upsertMeta('meta[property="og:title"]', "property", "og:title", _ogTitle);
    }
    if (_ogDesc) {
      upsertMeta('meta[property="og:description"]', "property", "og:description", _ogDesc);
    }
    if (_twTitle) {
      upsertMeta('meta[name="twitter:title"]', "name", "twitter:title", _twTitle);
    }
    if (_twDesc) {
      upsertMeta('meta[name="twitter:description"]', "name", "twitter:description", _twDesc);
    }

    // Canonical + og:url — always route-aware. Falls to current pathname
    // when not explicitly passed. Prevents duplicate-content SEO issue.
    if (typeof window !== "undefined") {
      const path = canonical || window.location.pathname || "/";
      const fullUrl =
        (path.startsWith("http") ? path : SITE_ORIGIN + (path.startsWith("/") ? path : `/${path}`));
      upsertLink("canonical", fullUrl);
      upsertMeta('meta[property="og:url"]', "property", "og:url", fullUrl);
    }
  }, [title, description, ogTitle, ogDescription, twitterTitle, twitterDescription, canonical]);
}
